import { DatePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { MessageBoxComponent } from '@common/popups/message-box/message-box.component';
import { LayoutService } from '@common/services/layout.service';
import { ModalService } from '@common/services/modal.service';
import { DatePickerHandler } from '@common/UI/date-picker/date-picker.component';
import { SortableExtractor } from '@common/UI/sortable/sortable.component';
import { Filterable } from '@common/utilities/Filterable';
import { ApiService } from '@core/services/api.service';
import { DataService, DateRange, PaymentReport, PaymentType, ProductUnit } from '@core/services/data.service';
import { GroupsService } from '@core/services/groups.service';
import { PartnersService } from '@core/services/partners.service';
import { ServicesService } from '@core/services/services.service';
import { SynchronizationService } from '@core/services/synchronization.service';
import { Multilingual } from '@core/utilities/multilingual';
import { Storage } from '@core/utilities/storage';
import { map } from 'rxjs/operators';
import { PaymentReportsViewerComponent } from './payment-reports-viewer/payment-reports-viewer.component';


interface TimeGroup {
  Date: Date;
  Name: string;
  PartnerIncome: number;
  ServiceIncome: number;

  PartnerExpense: number;
  ServiceExpense: number;

  children: TimeGroup[];
  records: PaymentReport[];
  parent: TimeGroup;
}

interface SubjectGroup {
  Name: string;
  Income: number;
  Expense: number;
  children: SubjectGroup[];
  records: PaymentReport[];
  parent: SubjectGroup
}

@Component({
  templateUrl: './payment-report.component.html',
  styleUrls: ['./payment-report.component.scss']
})
export class PaymentReportComponent implements OnInit {

  constructor(private modal: ModalService, private layout: LayoutService, private ds: DataService, private sync: SynchronizationService, private groups: GroupsService, private services: ServicesService, private partners: PartnersService, private date: DatePipe) { }

  public get mode(): 'time' | 'subject' {
    return Storage.get(location.pathname, 'time');
  }

  public set mode(value: 'time' | 'subject') {
    Storage.set(location.pathname, value);
    switch (value) {
      case 'time': if (!this.paymentsByDate.empty) this.setPath(this.paymentsByDate.source[0].parent); break;
      case 'subject': if (!this.paymentsBySubject.empty) this.setPath(this.paymentsBySubject.source[0].parent); break;
    }
  }

  public selectMode() {
    this.modal.show(MessageBoxComponent, {
      state: {
        title: Multilingual.getText('Chọn hình thức hiển thị'),
        message: Multilingual.getText('Chọn một trong các hình thức hiển thị dữ liệu dưới đây') + ':',
        buttons: [
          { name: Multilingual.getText('Hiển thị theo thời gian'), class: this.mode == 'time' ? '' : 'bg-primary text-white', click: () => this.mode = 'time', icon: this.mode == 'time' ? 'fa fa-check' : 'fa fa-clock' },
          { name: Multilingual.getText('Hiện thị theo đối tượng'), class: this.mode == 'subject' ? '' : 'bg-primary text-white', click: () => this.mode = 'subject', icon: this.mode == 'subject' ? 'fa fa-check' : 'fa fa-user-tie' }
        ]
      }
    });
  }

  public paymentsByDate = new Filterable<TimeGroup>({
    extractor: i => [i.Name]
  });

  public paymentsBySubject = new Filterable<SubjectGroup>({
    extractor: i => [i.Name]
  });

  public path: (SubjectGroup | TimeGroup)[] = [];

  public get loading(): boolean {
    return this.range && !this.sync.ready(PaymentReportComponent, ServicesService, GroupsService, PartnersService)
  }

  public range: { name: string, range: DateRange } = null;

  private setPath(group: TimeGroup | SubjectGroup) {
    let path: (TimeGroup | SubjectGroup)[] = [group];

    while (group.parent) {

      path.push(group.parent);
      group = group.parent;
    }

    this.path = path.reverse();
  }

  public open(group: TimeGroup | SubjectGroup) {

    if (group.children.length > 0) {

      switch (this.mode) {
        case 'time':

          this.paymentsByDate.source = group.children as TimeGroup[];          
          break;

        case 'subject':

          this.paymentsBySubject.source = group.children as SubjectGroup[];
          break;
      }

      this.setPath(group);
    }
  }

  public dgExtractor(field: string): SortableExtractor<TimeGroup> {
    return {
      extract: i => {
        switch (field) {
          case 'Date': return i.Date.getTime();
          case 'Total': return i.PartnerIncome + i.ServiceIncome - i.PartnerExpense - i.ServiceExpense;
          default: return i[field];
        }
      }
    }
  }

  public sgExtractor(field: string): SortableExtractor<SubjectGroup> {
    return {
      extract: i => {
        switch (field) {
          case 'Total': return i.Income - i.Expense;
          default: return i[field];
        }
      }
    }
  }

  public dgSum(field: string): number {
    switch (field) {
      case 'Total': return this.paymentsByDate.matches.sum(i => i.PartnerIncome + i.ServiceIncome - i.PartnerExpense - i.ServiceExpense);
      default: return this.paymentsByDate.matches.sum(i => i[field]);
    }
  }

  public sgSum(field: string): number {
    switch (field) {
      case 'Total': return this.paymentsBySubject.matches.sum(i => i.Income - i.Expense);
      default: return this.paymentsBySubject.matches.sum(i => i[field]);
    }
  }

  public count(group: { records: PaymentReport[] }, category: 'partner' | 'service' | 'any', type: 'income' | 'expense'): number {
    return group.records.count(i => i.Type == (category == 'partner' ? PaymentType.Partner : category == 'service' ? PaymentType.Service : i.Type) && i.Amount * (type == 'income' ? 1 : -1) > 0);
  }

  public view(group: { Name: string, records: PaymentReport[] }, category: 'partner' | 'service' | 'any', type: 'income' | 'expense') {

    this.modal.show(PaymentReportsViewerComponent, {
      state: {
        reports: group.records.filter(i => i.Type == (category == 'partner' ? PaymentType.Partner : category == 'service' ? PaymentType.Service : i.Type) && i.Amount * (type == 'income' ? 1 : -1) > 0),
        title: Multilingual.getText(type == 'income'
          ? (category == 'partner' ? 'Phiếu thu đối tác' : category == 'service' ? 'Phiếu thu dịch vụ' : 'Phiếu thu')
          : type == 'expense'
            ? (category == 'partner' ? 'Phiếu chi đối tác' : category == 'service' ? 'Phiếu chi dịch vụ' : 'Phiếu chi')
            : (category == 'partner' ? 'Phiếu thu chi đối tác' : category == 'service' ? 'Phiếu thu chi dịch vụ' : 'Phiếu thu chi'))

          + ' ' + (this.mode == 'time' ? '(' + group.Name + ')' : group.Name + ' (' + this.range.name + ')')
      } 
    });
  }

  public handler: DatePickerHandler = {
    change: (info: { name: string, range: DateRange }) => {

      this.range = info;
      this.paymentsByDate.source = [];
      this.paymentsBySubject.source = [];

      this.sync.notify(PaymentReportComponent, false);

      return this.ds.reports('payments', { range: info.range }).pipe(map(report => {

        this.sync.notify(PaymentReportComponent, true);

        if (report && report.Payments) {

          this.sync.when(GroupsService, ServicesService, PartnersService, ApiService).commit(() => {

            let times: TimeGroup = { Name: Multilingual.getText('Thu chi') + ' ' + this.range.name, Date: new Date(), ServiceExpense: 0, ServiceIncome: 0, PartnerExpense: 0, PartnerIncome: 0, children: undefined, parent: null, records: undefined };

            times.children = report.Payments.group(i => new Date(i.Stamp).beginOfMonth().getTime()).map<TimeGroup>(m => {

              let month: TimeGroup =
              {
                Date: new Date(m.key as number),
                Name: Multilingual.getText('Tháng') + ' ' + this.date.transform(m.key as number, 'MM'),
                PartnerIncome: m.items.sum(i => i.Amount > 0 && i.Type == PaymentType.Partner ? i.Amount : 0),
                PartnerExpense: m.items.sum(i => i.Amount < 0 && i.Type == PaymentType.Partner ? -i.Amount : 0),
                ServiceIncome: m.items.sum(i => i.Amount > 0 && i.Type == PaymentType.Service ? i.Amount : 0),
                ServiceExpense: m.items.sum(i => i.Amount < 0 && i.Type == PaymentType.Service ? -i.Amount : 0),
                parent: times,
                records: m.items,
                children: undefined                
              };

              month.children = m.items.group(i => new Date(i.Stamp).date().getTime()).map<TimeGroup>(d => ({
                Date: new Date(d.key as number),
                Name: this.date.transform(d.key as number, 'dd-MM-yyyy'),
                PartnerIncome: d.items.sum(i => i.Amount > 0 && i.Type == PaymentType.Partner ? i.Amount : 0),
                PartnerExpense: d.items.sum(i => i.Amount < 0 && i.Type == PaymentType.Partner ? -i.Amount : 0),
                ServiceIncome: d.items.sum(i => i.Amount > 0 && i.Type == PaymentType.Service ? i.Amount : 0),
                ServiceExpense: d.items.sum(i => i.Amount < 0 && i.Type == PaymentType.Service ? -i.Amount : 0),
                parent: month,
                records: d.items,
                children: []
              }));

              return month;
            });

            this.paymentsByDate.source = times.children;

            let subjects: SubjectGroup = { Name: Multilingual.getText('Thu chi') + ' ' + this.range.name, children: undefined, Expense: 0, Income: 0, records: undefined, parent: null };

            subjects.children = report.Payments.group(i => i.Type).map<SubjectGroup>(root => {

              switch (root.key as number) {

                case PaymentType.Partner:
                  {
                    let partners: SubjectGroup = {
                      Name: Multilingual.getText("Đối tác"),
                      Income: root.items.sum(i => i.Amount > 0 ? i.Amount : 0),
                      Expense: root.items.sum(i => i.Amount < 0 ? -i.Amount : 0),
                      parent: subjects,
                      children: undefined,
                      records: root.items
                    };

                    partners.children = root.items.group(i => i.PartnerID).map<SubjectGroup>(partner => ({
                      Name: this.partners.get(partner.key as number).Name,
                      Income: partner.items.sum(i => i.Amount > 0 ? i.Amount : 0),
                      Expense: partner.items.sum(i => i.Amount < 0 ? -i.Amount : 0),
                      parent: partners,
                      children: [],
                      records: partner.items
                    }));

                    return partners;
                  }

                case PaymentType.Service:
                  {
                    let services: SubjectGroup = {
                      Name: Multilingual.getText("Dịch vụ"),
                      Income: root.items.sum(i => i.Amount > 0 ? i.Amount : 0),
                      Expense: root.items.sum(i => i.Amount < 0 ? -i.Amount : 0),
                      parent: subjects,
                      children: undefined,
                      records: root.items
                    };

                    services.children = root.items.group(i => this.services.get(i.PartnerID).GroupID).map<SubjectGroup>(g => {

                      let group = {
                        Name: this.groups.get(g.key as number).Name,
                        Income: g.items.sum(i => i.Amount > 0 ? i.Amount : 0),
                        Expense: g.items.sum(i => i.Amount < 0 ? -i.Amount : 0),
                        parent: services,
                        children: undefined,
                        records: g.items
                      }

                      group.children = g.items.group(i => i.PartnerID).map<SubjectGroup>(service => ({
                        Name: this.services.get(service.key as number).Name,
                        Income: service.items.sum(i => i.Amount > 0 ? i.Amount : 0),
                        Expense: service.items.sum(i => i.Amount < 0 ? -i.Amount : 0),
                        parent: group,
                        children: [],
                        records: service.items
                      }));

                      return group;
                    });

                    return services;
                  }
              }
            });

            this.paymentsBySubject.source = subjects.children;
            this.setPath(this.mode == 'time' ? times : subjects);
          });

          return true;
        }

        return false;
      }));
    }
  }

  ngOnInit(): void {
    this.layout.title = Multilingual.getText('Báo cáo thu chi');
  }

}
